import React, { useEffect, useState, useRef } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { FaFilter, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { FINANCIAL_YEAR_OPTIONS } from '../../../components/common/dateUtils';

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
};

const ROWS_PER_PAGE = 15;

// Define all possible Type of Purchase options - Always visible
const ALL_TYPE_OF_PURCHASE = ['Origin','High Sea','Domestic'];

const normalizeCurrency = (value) =>
  (value || '').toString().trim().toUpperCase();

const normalizeTypeOfPurchase = (value) =>
  (value || '').toString().trim().toLowerCase();

const CountryWiseReport = () => {
  const [records, setRecords] = useState([]);
  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const initialLoadRef = useRef(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    financialYear: '',
    supplier: '',
    country: '',
    contractNo: '',
    commodity: '',
    commodity_origin: '',
    state: '',
    contractDateFrom: '',
    contractDateTo: '',
    typeOfPurchase: [],
    currency: ''
  });
  
  // Unique values for dropdowns (removed typeOfPurchases)
  const [uniqueValues, setUniqueValues] = useState({
    suppliers: [],
    countries: [],
    commodities: [],
    origins: [],
    states: [],
    currencies: []
  });

  const fetchRecords = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/report/country-wise-report`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch report');
      const result = await res.json();
      
      const data = result.data || [];
      setAllRecords(data);
      setRecords(data);
      setTotal(data.length || 0);
      initialLoadRef.current = true;
      
      // Extract unique values for filters (removed typeOfPurchases extraction)
      const suppliers = [...new Set(data.map(item => item.supplier_name).filter(Boolean))];
      const countries = [...new Set(data.map(item => item.supplier_country).filter(Boolean))];
      const commodities = [...new Set(data.map(item => item.commodity_name).filter(Boolean))];
      const origins = [...new Set(data.map(item => item.commodity_origin).filter(Boolean))];
      const states = [...new Set(data.map(item => item.state_of_purchase).filter(Boolean))];
      const currencies = [
        ...new Set(
          data
            .map(item => normalizeCurrency(item.currency))
            .filter(Boolean)
        ),
      ];
      
      setUniqueValues({
        suppliers,
        countries,
        commodities,
        origins,
        states,
        currencies
      });
    } catch (err) {
      setError('Failed to load report data');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const nextValue =
      name === 'currency' ? normalizeCurrency(value) : value;

    setFilters(prev => ({
      ...prev,
      [name]: nextValue
    }));
  };

  // Reset all filters function
  const handleResetFilters = () => {
    setFilters({
      financialYear: '',
      supplier: '',
      country: '',
      contractNo: '',
      commodity: '',
      commodity_origin: '',
      state: '',
      contractDateFrom: '',
      contractDateTo: '',
      typeOfPurchase: [],
      currency: ''
    });
    setPage(1);
  };

  const toggleTypeOfPurchase = (type) => {
    const normalizedType = normalizeTypeOfPurchase(type);
    setFilters((prev) => {
      const exists = prev.typeOfPurchase.includes(normalizedType);
      const updated = exists
        ? prev.typeOfPurchase.filter((item) => item !== normalizedType)
        : [...prev.typeOfPurchase, normalizedType];
      return {
        ...prev,
        typeOfPurchase: updated,
      };
    });
  };

  useEffect(() => {
    if (!initialLoadRef.current) {
      return;
    }

    const filtersActive =
      filters.financialYear ||
      filters.supplier ||
      filters.country ||
      filters.contractNo ||
      filters.commodity ||
      filters.commodity_origin ||
      filters.state ||
      filters.contractDateFrom ||
      filters.contractDateTo ||
      filters.typeOfPurchase.length > 0 ||
      filters.currency;

    if (!filtersActive) {
      setRecords(allRecords);
      setTotal(allRecords.length);
      setError(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    const applyFilters = async () => {
      const queryParams = new URLSearchParams();

      if (filters.financialYear) queryParams.append('financialYear', filters.financialYear);
      if (filters.supplier) queryParams.append('supplier', filters.supplier);
      if (filters.country) queryParams.append('country', filters.country);
      if (filters.contractNo) queryParams.append('contractNo', filters.contractNo);
      if (filters.commodity) queryParams.append('commodity', filters.commodity);
      if (filters.commodity_origin) queryParams.append('commodity_origin', filters.commodity_origin);
      if (filters.state) queryParams.append('state', filters.state);
      if (filters.contractDateFrom) queryParams.append('contractDateFrom', filters.contractDateFrom);
      if (filters.contractDateTo) queryParams.append('contractDateTo', filters.contractDateTo);
      if (filters.currency) queryParams.append('currency', filters.currency);
      if (filters.typeOfPurchase.length > 0) {
        filters.typeOfPurchase.forEach((type) =>
          queryParams.append('typeOfPurchase', type)
        );
      }

      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/report/country-wise-report?${queryParams.toString()}`,
          {
            credentials: 'include',
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          throw new Error('Failed to apply filters');
        }

        const result = await response.json();
        const data = result.data || [];

        if (!controller.signal.aborted) {
          setRecords(data);
          setTotal(data.length || 0);
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error('Apply filters error:', err);
        setError('Failed to apply filters');
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    applyFilters();

    return () => controller.abort();
  }, [filters, allRecords]);

  const totalPages = Math.ceil(total / ROWS_PER_PAGE);
  const paginatedRecords = records.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  const formatNumber = (number, decimals = 2) => {
    if (number === null || number === undefined || number === '') return '-';
    return parseFloat(number).toFixed(decimals);
  };

  const truncateToTwoWords = (text) => {
    if (!text) return '-';
    const words = text.split(' ');
    return words.slice(0, 3).join(' ') + (words.length > 3 ? '...' : '');
  };

  const calculateGrandTotal = (data) => {
    return data.reduce((total, record) => ({
      qty: total.qty + (parseFloat(record.contract_qty) || 0),
      amount_usd: total.amount_usd + (parseFloat(record.contract_amount_usd) || 0),
      amount_inr: total.amount_inr + (parseFloat(record.contract_amount_inr) || 0),
      count: total.count + 1
    }), { qty: 0, amount_usd: 0, amount_inr: 0, count: 0 });
  };

  const formatShipmentPeriod = (startDate, endDate) => {
    if (!startDate && !endDate) return '-';
    if (!startDate) return `To ${formatDate(endDate)}`;
    if (!endDate) return `From ${formatDate(startDate)}`;
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const grandTotal = calculateGrandTotal(records);

  return (
    <div className="bg-white p-3 sm:p-4 rounded overflow-x-auto">
      <div className="mb-4 flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-700 text-center sm:text-left">Purchase Contracts</h2>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-[#10a5e9] rounded-md hover:bg-blue-100 text-sm w-full sm:w-auto justify-center"
        >
          <FaFilter />
          <span className="hidden sm:inline">{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
          <span className="sm:hidden">{showFilters ? 'Hide' : 'Filters'}</span>
          {showFilters ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
        </button>
      </div>

      {showFilters && (
        <div className="mb-6 p-3 sm:p-4 border border-gray-200 rounded-lg bg-gray-50">
          {/* Reset Button */}
          <div className="flex justify-end mb-3">
            <button
              onClick={handleResetFilters}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition text-sm w-full sm:w-auto"
            >
             
              Reset Filters
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 items-end">
            <div className="flex flex-col">
              <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">Financial Year</label>
              <select
                name="financialYear"
                value={filters.financialYear}
                onChange={handleFilterChange}
                className="px-3 py-2 border border-[#10a5e9] rounded-md shadow-sm focus:outline-none focus:ring-[#10a5e9] focus:border-[#10a5e9] text-sm bg-white font-medium text-gray-800"
              >
                <option value="">All Financial Years</option>
                {FINANCIAL_YEAR_OPTIONS.map((opt) => (
                  <option key={opt.id} value={opt.value}>
                    {opt.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">Supplier</label>
              <select
                name="supplier"
                value={filters.supplier}
                onChange={handleFilterChange}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#10a5e9] focus:border-[#10a5e9] text-sm"
              >
                <option value="">All Suppliers</option>
                {uniqueValues.suppliers.map(supplier => (
                  <option key={supplier} value={supplier}>{supplier}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">Country</label>
              <select
                name="country"
                value={filters.country}
                onChange={handleFilterChange}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#10a5e9] focus:border-[#10a5e9] text-sm"
              >
                <option value="">All Countries</option>
                {uniqueValues.countries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">Contract No</label>
              <input
                type="text"
                name="contractNo"
                value={filters.contractNo}
                onChange={handleFilterChange}
                placeholder="Search contract no"
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#10a5e9] focus:border-[#10a5e9] text-sm"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">Contract Date From</label>
              <input
                type="date"
                name="contractDateFrom"
                value={filters.contractDateFrom}
                onChange={handleFilterChange}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#10a5e9] focus:border-[#10a5e9] text-sm"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">Contract Date To</label>
              <input
                type="date"
                name="contractDateTo"
                value={filters.contractDateTo}
                onChange={handleFilterChange}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#10a5e9] focus:border-[#10a5e9] text-sm"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">Commodity</label>
              <select
                name="commodity"
                value={filters.commodity}
                onChange={handleFilterChange}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#10a5e9] focus:border-[#10a5e9] text-sm"
              >
                <option value="">All Commodities</option>
                {uniqueValues.commodities.map(commodity => (
                  <option key={commodity} value={commodity}>{commodity}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">Origin</label>
              <select
                name="commodity_origin"
                value={filters.commodity_origin}
                onChange={handleFilterChange}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#10a5e9] focus:border-[#10a5e9] text-sm"
              >
                <option value="">All Origins</option>
                {uniqueValues.origins.map(origin => (
                  <option key={origin} value={origin}>{origin}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">State</label>
              <select
                name="state"
                value={filters.state}
                onChange={handleFilterChange}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#10a5e9] focus:border-[#10a5e9] text-sm"
              >
                <option value="">All States</option>
                {uniqueValues.states.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>

            {/* Updated Type of Purchase - Static Options */}
            <div className="flex flex-col">
              <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">Type of Purchase</label>
              <div className="flex flex-wrap gap-2">
                {ALL_TYPE_OF_PURCHASE.map(type => {
                  const normalizedType = normalizeTypeOfPurchase(type);
                  const isSelected = filters.typeOfPurchase.includes(normalizedType);
                  return (
                    <button
                      type="button"
                      key={type}
                      onClick={() => toggleTypeOfPurchase(type)}
                      className={`px-3 py-1 text-xs sm:text-sm rounded-full border transition-colors ${
                        isSelected
                          ? 'bg-[#10a5e9] text-white border-[#10a5e9]'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-[#10a5e9] hover:text-[#10a5e9]'
                      }`}
                    >
                      {type}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col">
              <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">Currency</label>
              <select
                name="currency"
                value={filters.currency}
                onChange={handleFilterChange}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#10a5e9] focus:border-[#10a5e9] text-sm"
              >
                <option value="">All Currencies</option>
                {uniqueValues.currencies.map(currency => (
                  <option key={currency} value={currency}>{currency}</option>
                ))}
              </select>
            </div>
            
          </div>
          
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center h-48">
          <div className="animate-spin h-8 w-8 sm:h-10 sm:w-10 border-4 border-[#10a5e9] border-t-transparent rounded-full mb-2" />
          <span className="text-gray-600 text-xs sm:text-sm">Loading contract data...</span>
        </div>
      ) : error ? (
        <div className="text-center text-red-600 py-6 text-xs sm:text-sm">{error}</div>
      ) : records.length === 0 ? (
        <div className="text-center text-gray-500 py-6 text-xs sm:text-sm">
          {Object.values(filters).some(filter => filter !== '' && (Array.isArray(filter) ? filter.length > 0 : true)) 
            ? "No contracts match your filters" 
            : "No purchase contract data available"}
        </div>
      ) : (
        <div className="overflow-x-auto overflow-y-auto max-h-96" style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f1f5f9' }}>
          <style jsx>{`
            div::-webkit-scrollbar {
              width: 6px;
              height: 6px;
            }
            div::-webkit-scrollbar-track {
              background: #f1f5f9;
            }
            div::-webkit-scrollbar-thumb {
              background: #cbd5e1;
              border-radius: 3px;
            }
            div::-webkit-scrollbar-thumb:hover {
              background: #94a3b8;
            }
          `}</style>
          <table className="min-w-max w-full text-xs sm:text-sm border-collapse border border-gray-300">
            <thead className="bg-gray-100 sticky top-0">
              <tr className="text-left text-gray-600">
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[90px]">
                  <span className="hidden sm:inline">Supplier</span>
                  <span className="sm:hidden">Supp</span>
                </th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[70px] hidden md:table-cell">Country</th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[85px]">
                  <span className="hidden sm:inline">Contract No</span>
                  <span className="sm:hidden">Contract</span>
                </th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[80px] hidden lg:table-cell">Contract Date</th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[90px] hidden xl:table-cell">Commodity</th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[70px] hidden xl:table-cell">Origin</th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[70px] hidden lg:table-cell">State</th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[85px] hidden xl:table-cell">Shipment Period</th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[70px]">
                  <span className="hidden sm:inline">Qty (MT)</span>
                  <span className="sm:hidden">Qty</span>
                </th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[90px] hidden lg:table-cell" title="Type of Purchase">Type of Pur..</th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[70px] hidden md:table-cell">CUR</th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[70px] hidden md:table-cell">Price</th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[80px] hidden lg:table-cell">Amount</th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[75px] hidden xl:table-cell">Ex.Rate</th>
                <th className="px-2 sm:px-3 py-2 border border-gray-300 min-w-[80px] hidden md:table-cell">Amount (INR)</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRecords.map((item) => (
                <tr key={item.purchase_contract_no} className="hover:bg-blue-50">
                  <td className="px-2 sm:px-3 py-2 border border-gray-200 cursor-pointer text-xs sm:text-sm" title={item.supplier_name || '-'}>
                    <div className="font-medium">{truncateToTwoWords(item.supplier_name)}</div>
                    <div className="text-xs text-gray-500 sm:hidden">{item.supplier_country || '-'}</div>
                  </td>
                  <td className="px-2 sm:px-3 py-2 border border-gray-200 text-xs sm:text-sm hidden md:table-cell">{item.supplier_country || '-'}</td>
                  <td className="px-2 sm:px-3 py-2 border border-gray-200 text-xs sm:text-sm">{item.purchase_contract_no}</td>
                  <td className="px-2 sm:px-3 py-2 border border-gray-200 text-xs sm:text-sm hidden lg:table-cell">{item.contract_date ? formatDate(item.contract_date) : '-'}</td>
                  <td className="px-2 sm:px-3 py-2 border border-gray-200 cursor-pointer text-xs sm:text-sm hidden xl:table-cell" title={item.commodity_name || '-'}>
                    {truncateToTwoWords(item.commodity_name)}
                  </td>
                  <td className="px-2 sm:px-3 py-2 border border-gray-200 text-xs sm:text-sm hidden xl:table-cell">{item.commodity_origin || '-'}</td>
                  <td className="px-2 sm:px-3 py-2 border border-gray-200 text-xs sm:text-sm hidden lg:table-cell">{item.state_of_purchase || '-'}</td>
                  <td className="px-2 sm:px-3 py-2 border border-gray-200 text-xs sm:text-sm hidden xl:table-cell">{formatShipmentPeriod(item.expected_shipment_start, item.expected_shipment_end)}</td>
                  <td className="px-2 sm:px-3 py-2 border border-gray-200 text-right text-xs sm:text-sm">
                    <div className="font-medium">{formatNumber(item.contract_qty, 3)}</div>
                    <div className="text-xs text-gray-500 sm:hidden">{item.currency === 'USD' ? '$' : '₹'}{formatNumber(item.price, 2)}</div>
                  </td>
                  <td className="px-2 sm:px-3 py-2 border border-gray-200 text-xs sm:text-sm hidden lg:table-cell" title={item.type_of_purchase || '-'}>{item.type_of_purchase || '-'}</td>
                  <td className="px-2 sm:px-3 py-2 border border-gray-200 text-xs sm:text-sm hidden md:table-cell" title={item.currency || '-'}>{item.currency || '-'}</td>
                  <td className="px-2 sm:px-3 py-2 border border-gray-200 text-xs sm:text-sm hidden md:table-cell">
  <div className="flex justify-between">
    <span>
      {item.currency === 'USD' ? '$' : '₹'}
    </span>
    <span className="text-right">
      {formatNumber(item.price, 2)}
    </span>
  </div>
</td>

                  <td className="px-2 sm:px-3 py-2 border border-gray-200 text-xs sm:text-sm hidden lg:table-cell">
  <div className="flex justify-between">
    <span>
      {item.currency === 'USD' ? '$' : '₹'}
    </span>
    <span className="text-right">
      {formatNumber(item.contract_amount_usd, 2)}
    </span>
  </div>
</td>

                  <td className="px-2 sm:px-3 py-2 border border-gray-200 text-right text-xs sm:text-sm hidden xl:table-cell" title={formatNumber(item.exchange_rate, 4)}>{formatNumber(item.exchange_rate, 4)}</td>
                  <td className="px-2 sm:px-3 py-2 border border-gray-200 text-right text-xs sm:text-sm hidden md:table-cell">{formatNumber(item.contract_amount_inr, 2)}</td>
                </tr>
              ))}
              
              {(page === totalPages || totalPages === 0) && records.length > 0 && (
                <tr className="bg-blue-100 font-bold text-blue-900">
                  <td className="px-2 sm:px-3 py-3 border border-gray-300" colSpan="8">
                    <span className="hidden sm:inline">GRAND TOTAL ({grandTotal.count} contracts)</span>
                    <span className="sm:hidden">TOTAL ({grandTotal.count})</span>
                  </td>
                  <td className="px-2 sm:px-3 py-3 border border-gray-300 text-right font-bold text-xs sm:text-sm">
                    {formatNumber(grandTotal.qty, 3)}
                  </td>
                  <td className="px-2 sm:px-3 py-3 border border-gray-300 text-xs sm:text-sm hidden lg:table-cell">-</td>
                  <td className="px-2 sm:px-3 py-3 border border-gray-300 text-xs sm:text-sm hidden md:table-cell">-</td>
                  <td className="px-2 sm:px-3 py-3 border border-gray-300 text-right text-xs sm:text-sm hidden md:table-cell">
                    {grandTotal.qty > 0 ? formatNumber(grandTotal.amount_usd / grandTotal.qty, 2) : '-'}
                  </td>
                  <td className="px-2 sm:px-3 py-3 border border-gray-300 text-right font-bold text-xs sm:text-sm hidden lg:table-cell">
                    {formatNumber(grandTotal.amount_usd, 2)}
                  </td>
                  <td className="px-2 sm:px-3 py-3 border border-gray-300 text-right text-xs sm:text-sm hidden xl:table-cell">
                    {grandTotal.amount_usd > 0 
                      ? formatNumber(grandTotal.amount_inr / grandTotal.amount_usd, 2) 
                      : "-"}
                  </td>
                  <td className="px-2 sm:px-3 py-3 border border-gray-300 text-right font-bold text-xs sm:text-sm hidden md:table-cell">
                    {formatNumber(grandTotal.amount_inr, 2)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {total > ROWS_PER_PAGE && (
        <div className="flex flex-col sm:flex-row justify-center sm:justify-end items-center gap-3 sm:gap-2 mt-3 text-xs">
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrevPage}
              disabled={page === 1}
              className={`px-3 sm:px-2 py-2 sm:py-1 rounded border transition-colors duration-100 ${page === 1 ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-blue-500 border-blue-500 hover:text-white hover:bg-blue-500'}`}
            >
              <span className="hidden sm:inline">Prev</span>
              <span className="sm:hidden">Previous</span>
            </button>
            <span className="text-gray-600 px-2">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={handleNextPage}
              disabled={page === totalPages}
              className={`px-3 sm:px-2 py-2 sm:py-1 rounded border transition-colors duration-100 ${page === totalPages ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-blue-500 border-blue-500 hover:text-white hover:bg-blue-500'}`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryWiseReport;
